import { useCallback, useEffect, useRef, useState } from 'react';
import { useClaudeService } from '../contexts/ClaudeServiceContext';
import { isDocumentComplete } from '../utils/briefCompletion';
import { parseComponentPayload } from '../utils/parseComponentPayload';
import { resolveCtaUrl } from '../utils/resolveCtaUrl';
import type { BriefDocument, Message, TextMessage } from '../types/chat';

const PREVIEWABLE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);

/** MIME types the Claude agent accepts as inline base64 images. */
export const AGENT_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png']);

export function extractUserText(msg: Message): string {
    switch (msg.type) {
        case 'text':
            return msg.content;
        case 'imageAttachment':
            return JSON.stringify({
                type: 'image-upload',
                data: {
                    value: msg.images.map((att) => ({
                        bucket: 'CimpressDocs',
                        key: att.uploadId,
                        name: att.fileName,
                        previewable: PREVIEWABLE_EXTENSIONS.has(
                            att.fileName.split('.').pop()?.toLowerCase() ?? ''
                        ),
                        size: att.fileSize != null ? Number(att.fileSize) : undefined,
                    })),
                    // Only forward image types the agent can process as inline base64.
                    // Other file types (PDF, DOC, BMP, GIF) are referenced by key only.
                    images: msg.images
                        .filter((att) =>
                            AGENT_IMAGE_MIME_TYPES.has(att.compressedMediaType ?? att.mediaType)
                        )
                        .map((att) => ({
                            type: 'base64',
                            media_type: att.compressedMediaType ?? att.mediaType,
                            data: att.base64 ?? '',
                        })),
                    message: msg.caption ?? '',
                },
            });
        case 'componentMessage': {
            return JSON.stringify({ type: msg.componentType, data: msg.data });
        }
        case 'componentRequest':
            return '';
    }
}

interface UseClaudeTurnOptions {
    briefId: string;
    /** Append agent messages to the log. */
    append: (...msgs: Message[]) => void;
    initialBriefComplete?: boolean;
    initialMaxMessages?: number | null;
    initialMessages?: Message[];
}

/** One round-trip with the chat transport. Owns the `isLoading` / `error` state. */
export function useClaudeTurn({
    briefId,
    append,
    initialBriefComplete,
    initialMaxMessages,
    initialMessages,
}: UseClaudeTurnOptions) {
    const sendMessageToClaude = useClaudeService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isStaleSession, setIsStaleSession] = useState(false);
    const [isFatalError, setIsFatalError] = useState(false);
    const [briefComplete, setBriefComplete] = useState(initialBriefComplete ?? false);
    const briefCompleteOverrideRef = useRef<boolean>(initialBriefComplete ?? false);
    const [document, setDocument] = useState<BriefDocument | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [maxMessages, _setMaxMessages] = useState<number | null>(initialMaxMessages ?? null);
    const lastConversationRef = useRef<Message[]>([]);

    useEffect(() => {
        if (initialMaxMessages == null || !initialMessages) return;
        const count = initialMessages.filter((m) => m.type === 'text').length;
        if (count >= initialMaxMessages) {
            append({
                type: 'text',
                role: 'agent',
                content:
                    "Sorry, that's as far as this chat can go — we've reached our message limit.",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runTurn = useCallback(
        async (conversation: Message[]): Promise<boolean> => {
            lastConversationRef.current = conversation;
            setIsLoading(true);
            setError(null);
            setIsStaleSession(false);
            setIsFatalError(false);
            try {
                const lastUserMessage = [...conversation]
                    .reverse()
                    .find((msg) => msg.role === 'user');
                const message = lastUserMessage ? extractUserText(lastUserMessage) : '';
                // expectedMessageCount = number of non-componentRequest messages the server
                // already has, i.e. all non-componentRequest messages in the conversation
                // except the current user message being sent.
                const expectedMessageCount =
                    conversation.filter((m) => m.type !== 'componentRequest').length - 1;
                const response = await sendMessageToClaude(briefId, message, expectedMessageCount);
                const newMessages: Message[] = [];

                const parsedComponent = response.component
                    ? parseComponentPayload(response.component)
                    : null;

                let textContent = response.content ?? '';
                if (response.content) {
                    try {
                        const parsed = JSON.parse(response.content);
                        if (parsed && typeof parsed === 'object') {
                            if (typeof parsed.message === 'string') {
                                textContent = parsed.message;
                            }
                            if (parsed.inferredState?.isBriefComplete === true) {
                                briefCompleteOverrideRef.current = true;
                                setBriefComplete(true);
                            }
                        }
                    } catch {
                        textContent = response.content;
                    }
                }

                if (response.isBriefComplete === true) {
                    briefCompleteOverrideRef.current = true;
                    setBriefComplete(true);
                }

                if (response.document) {
                    setDocument(response.document);
                    if (!briefCompleteOverrideRef.current) {
                        setBriefComplete(isDocumentComplete(response.document));
                    }
                }

                if (response.content) {
                    const textMessage: TextMessage = {
                        type: 'text',
                        role: 'agent',
                        content: textContent,
                    };

                    if (parsedComponent?.type === 'cta-link') {
                        const ctaLink = resolveCtaUrl(
                            parsedComponent.urlKey,
                            parsedComponent.label
                        );
                        if (ctaLink) {
                            textMessage.ctaLink = ctaLink;
                        }
                    }

                    newMessages.push(textMessage);
                }

                if (parsedComponent && parsedComponent.type !== 'cta-link') {
                    newMessages.push({
                        type: 'componentRequest',
                        role: 'agent',
                        component: parsedComponent,
                    });
                }

                if (newMessages.length > 0) append(...newMessages);

                const totalMessageCount =
                    conversation.filter((m) => m.type === 'text').length +
                    newMessages.filter((m) => m.type === 'text').length;
                if (maxMessages != null && totalMessageCount >= maxMessages) {
                    append({
                        type: 'text',
                        role: 'agent',
                        content:
                            "Sorry, that's as far as this chat can go — we've reached our message limit.",
                    });
                }

                setRetryCount(0);
                return true;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to send message';
                if (message.startsWith('aborted:')) {
                    setIsStaleSession(true);
                } else if (
                    message.startsWith('invalid_argument:') ||
                    message.startsWith('failed_precondition:')
                ) {
                    const detail = message.slice(message.indexOf(':') + 1).trim();
                    setError(`Sending failed: ${detail}`);
                    setIsFatalError(true);
                } else {
                    setError(message);
                    setRetryCount((c) => c + 1);
                }
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [append, briefId, sendMessageToClaude, maxMessages]
    );

    const retry = useCallback(() => runTurn(lastConversationRef.current), [runTurn]);

    return {
        runTurn,
        retry,
        isLoading,
        error,
        isStaleSession,
        isFatalError,
        retryCount,
        briefComplete,
        document,
        maxMessages,
    };
}
